/**
 * ProfileCompletionBanner (PR #4)
 *
 * Shown on parent layout when Family.metadata.profileComplete is not true.
 *
 * Backend (PR #3c) sets the flag during invitation completion. For families
 * onboarded under the legacy admin-create + minimal-signup flow, the flag is
 * undefined (treated as not complete) and the banner appears, prompting them
 * to fill in extended fields via /profile and /children edit modals.
 *
 * Dismissible per session (sessionStorage) to avoid nagging — but only soft
 * dismissal; the banner reappears next session until profileComplete=true.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useFamily } from '../hooks/useFamilies';

const SESSION_KEY = 'profile-completion-banner-dismissed';

export default function ProfileCompletionBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customClaims } = useAuth();
  const { family, isLoading } = useFamily(customClaims?.familyId);

  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Auto-hide if family becomes complete in another tab/session.
  useEffect(() => {
    if (family?.metadata?.profileComplete === true) {
      try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
    }
  }, [family?.metadata?.profileComplete]);

  if (isLoading || !family) return null;
  if (family.metadata?.profileComplete === true) return null;
  if (dismissed) return null;

  const handleComplete = () => {
    navigate('/profile/complete');
  };

  const handleDismiss = () => {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      role="alert"
      className="bg-amber-50 border-b border-amber-200 px-4 py-3"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start sm:items-center gap-3 min-w-0">
          <svg
            className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5 sm:mt-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z" />
          </svg>
          <div className="text-sm text-amber-900">
            <p className="font-medium">
              {t('profileCompletion.banner.title', { defaultValue: 'Профилът на семейството е непълен' })}
            </p>
            <p className="mt-0.5">
              {t('profileCompletion.banner.description', {
                defaultValue:
                  'Допълнете данните за родителите и децата за да получите достъп до всички функции (декларации, такси).',
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={handleComplete}
            className="px-4 py-1.5 text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
          >
            {t('profileCompletion.banner.action', { defaultValue: 'Допълни сега' })}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t('profileCompletion.banner.dismiss', { defaultValue: 'Затвори банера' })}
            className="text-amber-700 hover:text-amber-900 p-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
